/**
 * Record movement as a side drawer in three steps (Movements idea 6): 1 what happened (type), 2 the
 * route From → To with what is available in each store, 3 the items (typed or pasted) with an
 * availability pill per line. Changing the Site DN's project asks before items are taken off, and
 * the recorded DN prints as a real document. The form state lives in InventoryPage.
 */
import { useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, ClipboardPaste, Lock, Plus, Printer, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidePanel } from '@/components/ui/side-panel';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { INVENTORY_LOCATIONS, formatQty, plural } from './inventoryModel';

const fieldClass = 'rounded-control border-line bg-surface text-ink';
const labelClass = 'mb-1 block text-[12px] font-semibold text-ink-3';
const ADJUST_REASONS = ['Count correction', 'Damaged', 'Found'];

function Step({ n, title, hint, children }) {
  return (
    <section className="relative pl-9" aria-label={title}>
      <span className="absolute left-0 top-0 grid h-6 w-6 place-items-center rounded-full bg-brand text-[12px] font-bold text-white" aria-hidden="true">{n}</span>
      <p className="text-[13.5px] font-bold text-ink">{title}{hint ? <span className="ml-2 text-[12px] font-normal text-ink-3">{hint}</span> : null}</p>
      <div className="mt-2.5">{children}</div>
    </section>
  );
}

export default function RecordMovementPanel({
  open,
  onClose,
  types,
  form,
  setForm,
  config,
  onTypeChange,
  projects = [],
  onProjectChange,
  boqStatus,
  droppedParts = [],
  pendingDrop,
  onConfirmDrop,
  onKeepProject,
  rows,
  onRowChange,
  onAddRow,
  onRemoveRow,
  onPasteRows,
  catalogOptions,
  getItemLabel,
  getRowAvailability,
  sourceAvailability,
  stockNote = '',
  normalizePartNumber,
  onSubmit,
  submitting = false,
  deliveryPreview,
  onPrintPreview,
  onClosePreview,
  planLabel
}) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [paste, setPaste] = useState('');
  const type = form.movement_type;
  const filledRows = rows.filter((row) => row.part_number && Number(row.qty) !== 0);
  const totalQty = filledRows.reduce((sum, row) => sum + Math.abs(Number(row.qty) || 0), 0);
  const dirty = !deliveryPreview && filledRows.length > 0;
  const blocked = Boolean(pendingDrop);

  const sourceLabel = (location) => {
    const available = sourceAvailability?.(location);
    return available === null || available === undefined ? location : `${location} — ${formatNumber(available)} available`;
  };

  const footer = deliveryPreview ? null : (
    <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px] sm:py-3.5">
      <span className="min-w-0 flex-1 text-[12.5px] text-ink-3">
        {filledRows.length ? <><b className="font-semibold text-ink-2">{plural(filledRows.length, 'line')}</b> · {formatQty(totalQty)} pcs</> : 'No items yet'}
      </span>
      <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
      <Button type="submit" form="movement-form" disabled={submitting || blocked} data-testid="submit-movement-btn">
        {submitting ? 'Recording…' : type === 'issue' ? 'Record Site DN' : 'Record movement'}
      </Button>
    </footer>
  );

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => { if (!next) onClose(); }}
      width="lg"
      title="Record stock movement"
      subtitle={planLabel || 'Every change to stock is a movement in the ledger — with who, when and why.'}
      dirty={dirty}
      footer={footer}
      hideFooter={Boolean(deliveryPreview)}
      confirmOptions={{ title: 'Discard this movement?', description: 'The items you entered are not recorded yet.', confirmLabel: 'Discard' }}
      testId="movement-panel"
    >
      <form id="movement-form" onSubmit={onSubmit} className="space-y-6" data-testid="movement-form">
        <Step n={1} title="What happened">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Object.entries(types).map(([value, typeConfig]) => {
              const Icon = typeConfig.icon;
              const selected = type === value;
              return (
                <button
                  type="button"
                  key={value}
                  onClick={() => onTypeChange(value)}
                  data-testid={`movement-type-${value}`}
                  aria-pressed={selected}
                  disabled={Boolean(deliveryPreview)}
                  className={cn(
                    'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
                    selected ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink' : 'border-line bg-surface text-ink-2 hover:bg-surface-hover'
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block truncate">{typeConfig.stepLabel || typeConfig.shortLabel}</span>
                    <span className={cn('block truncate text-[11px] font-normal', selected ? 'text-brand-ink' : 'text-ink-3')} title={typeConfig.hint}>{typeConfig.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Step>

        <Step n={2} title={type === 'adjustment' ? 'Where and why' : 'From → To'}>
          <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)]">
            <div className="min-w-0">
              <span className={labelClass}>{type === 'adjustment' ? 'Location' : 'From'}</span>
              {config.needsSource ? (
                <Select
                  value={form.source_location}
                  onValueChange={(value) => setForm((prev) => ({
                    ...prev,
                    source_location: value,
                    destination_location: prev.movement_type === 'adjustment' ? value : prev.destination_location
                  }))}
                >
                  <SelectTrigger className={fieldClass} data-testid="movement-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVENTORY_LOCATIONS.filter((location) => type !== 'testing' || location !== 'Dubai Office Lab').map((location) => (
                      <SelectItem key={`movement-source-${location}`} value={location}>{sourceLabel(location)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex h-9 items-center rounded-control border border-line bg-surface-2 px-3 text-sm text-ink">
                  {type === 'GRN' ? 'Supplier' : type === 'return' ? 'Site / project' : '—'}
                </div>
              )}
            </div>
            {type === 'adjustment' ? <span /> : <ArrowRight className="mb-2.5 hidden h-4 w-4 text-ink-4 sm:block" aria-hidden="true" />}
            <div className="min-w-0">
              {type === 'adjustment' ? null : (
                <>
                  <span className={labelClass}>To</span>
                  {config.fixedDestination ? (
                    <div className="flex h-9 items-center rounded-control border border-line bg-surface-2 px-3 text-sm text-ink">{config.fixedDestination}</div>
                  ) : config.needsDestination ? (
                    <Select value={form.destination_location} onValueChange={(value) => setForm((prev) => ({ ...prev, destination_location: value }))}>
                      <SelectTrigger className={fieldClass} data-testid="movement-destination-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {INVENTORY_LOCATIONS.filter((location) => type !== 'transfer' || location !== form.source_location).map((location) => (
                          <SelectItem key={`movement-destination-${location}`} value={location}>{location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : config.needsProject ? (
                    <Select value={form.project_id} onValueChange={onProjectChange} disabled={Boolean(deliveryPreview)}>
                      <SelectTrigger data-testid="movement-project-select" className={fieldClass}><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {(config.needsReference || type === 'adjustment') ? (
              <label className="block">
                <span className={labelClass}>{type === 'adjustment' ? 'Reason · required' : type === 'GRN' ? 'Supplier DN / GRN number' : 'DN number'}</span>
                <Input
                  placeholder={type === 'adjustment' ? 'Adjustment reason' : 'Reference / DN Number'}
                  value={form.reference}
                  onChange={(event) => setForm((prev) => ({ ...prev, reference: event.target.value }))}
                  required={Boolean(config.needsReference) || type === 'adjustment'}
                  className={fieldClass}
                  maxLength={type === 'adjustment' ? 300 : 100}
                  data-testid="movement-reference-input"
                />
                {type === 'adjustment' ? (
                  <span className="mt-1.5 flex flex-wrap gap-1.5">
                    {ADJUST_REASONS.map((reason) => (
                      <button key={reason} type="button" onClick={() => setForm((prev) => ({ ...prev, reference: reason }))} className="rounded-full border border-line px-2 py-0.5 text-[11.5px] font-semibold text-ink-2 hover:bg-surface-hover">{reason}</button>
                    ))}
                  </span>
                ) : null}
              </label>
            ) : null}
            <label className={cn('block', !(config.needsReference || type === 'adjustment') && 'sm:col-span-2')}>
              <span className={labelClass}>Notes</span>
              <Input placeholder="Notes" value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className={fieldClass} />
            </label>
          </div>
        </Step>

        <Step
          n={3}
          title="Items"
          hint={type === 'adjustment' ? 'negative quantity removes stock' : config.needsSource ? `availability in ${form.source_location}` : undefined}
        >
          {type === 'issue' && form.project_id && boqStatus === 'loading' ? (
            <p className="mb-2 text-[12px] text-ink-3" data-testid="movement-boq-status">Loading project BOQ...</p>
          ) : null}
          {type === 'issue' && form.project_id && boqStatus === 'empty' ? (
            <p className="mb-2 text-[12px] text-status-amber-ink" data-testid="movement-boq-status">No BOQ found for this project — showing all items</p>
          ) : null}
          {type === 'issue' && form.project_id && boqStatus === 'error' ? (
            <p className="mb-2 text-[12px] text-status-red-ink" data-testid="movement-boq-status">Project BOQ could not be loaded — retry the project selection</p>
          ) : null}
          {pendingDrop ? (
            <div className="mb-3 rounded-xl border border-[color:var(--status-amber-bg)] bg-status-amber-bg px-3.5 py-3 text-[12.5px] text-ink" role="alert" data-testid="movement-drop-prompt">
              <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-amber-ink" aria-hidden="true" />
                <span>
                  <b className="font-bold">{plural(pendingDrop.parts.length, 'item')} {pendingDrop.parts.length === 1 ? 'is' : 'are'} not in {pendingDrop.projectName}&apos;s BOQ:</b>{' '}
                  <span className="font-mono text-ink-2">{pendingDrop.parts.join(', ')}</span>
                </span>
              </p>
              <div className="mt-2 flex flex-wrap gap-2 pl-5">
                <Button type="button" size="sm" onClick={onConfirmDrop} data-testid="movement-drop-confirm">Take them off this DN</Button>
                <Button type="button" size="sm" variant="outline" className="bg-surface text-ink" onClick={onKeepProject} data-testid="movement-drop-keep">
                  {pendingDrop.previousName ? `Keep ${pendingDrop.previousName}` : 'Pick another project'}
                </Button>
              </div>
            </div>
          ) : null}
          {type === 'issue' && droppedParts.length && !pendingDrop ? (
            <p className="mb-2 flex items-start gap-1.5 text-[12px] text-status-amber-ink" data-testid="movement-dropped-parts">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>Not in this project&apos;s BOQ, so taken off this DN: <span className="font-mono text-ink-2">{droppedParts.join(', ')}</span></span>
            </p>
          ) : null}

          <div className="space-y-2">
            {rows.map((row, index) => {
              const availability = getRowAvailability(row);
              const toneClass = availability.tone === 'red'
                ? 'bg-status-red-bg text-status-red-ink'
                : availability.tone === 'amber'
                  ? 'bg-status-amber-bg text-status-amber-ink'
                  : availability.tone === 'green'
                    ? 'bg-status-green-bg text-status-green-ink'
                    : 'border border-line bg-surface text-ink-3';
              return (
                <div key={index} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_84px_minmax(0,0.75fr)_32px] sm:items-center">
                  <Select
                    value={row.part_number}
                    onValueChange={(value) => onRowChange(index, 'part_number', value)}
                    disabled={(type === 'issue' && boqStatus === 'loading') || Boolean(deliveryPreview)}
                  >
                    <SelectTrigger className={cn(fieldClass, 'min-w-0 [&>span]:truncate')} data-testid={index === 0 ? 'movement-part-select' : undefined}>
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {catalogOptions.map((item) => (
                        <SelectItem key={normalizePartNumber(item.part_number)} value={item.part_number}>{getItemLabel(item)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={row.qty}
                    onChange={(event) => onRowChange(index, 'qty', event.target.value)}
                    required
                    className={cn(fieldClass, 'text-right')}
                    aria-label={`Quantity, line ${index + 1}`}
                    data-testid={index === 0 ? 'movement-qty-input' : undefined}
                  />
                  <span className={cn('flex h-9 min-w-0 items-center gap-1.5 rounded-control px-2.5 text-[12px] font-semibold', toneClass)} title={availability.text} data-testid={`movement-availability-${index}`}>
                    {availability.tone === 'green' ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : availability.tone === 'red' || availability.tone === 'amber' ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
                    <span className="truncate">{availability.text}</span>
                  </span>
                  <Button type="button" size="icon-sm" variant="ghost" disabled={rows.length === 1} onClick={() => onRemoveRow(index)} className="text-ink-3" aria-label={`Remove item row ${index + 1}`}>
                    <Trash2 aria-hidden="true" />
                  </Button>
                </div>
              );
            })}
          </div>
          {stockNote && !deliveryPreview ? (
            <p className="mt-2 flex items-start gap-1.5 text-[12px] text-status-blue-ink" data-testid="movement-booking-note">
              <Lock className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />{stockNote}
            </p>
          ) : null}
          <div className="mt-2.5 flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" className="text-ink" onClick={onAddRow} disabled={Boolean(deliveryPreview)}>
              <Plus aria-hidden="true" /> Add item
            </Button>
            <Button type="button" size="sm" variant="ghost" className="text-ink-2" onClick={() => setPasteOpen((value) => !value)} aria-expanded={pasteOpen} disabled={Boolean(deliveryPreview)}>
              <ClipboardPaste aria-hidden="true" /> Paste list
            </Button>
          </div>
          {pasteOpen ? (
            <div className="mt-2 rounded-xl border border-line bg-surface-2 p-3">
              <Textarea
                value={paste}
                onChange={(event) => setPaste(event.target.value)}
                placeholder={'Part no.\tQty\nMTN693003\t3\nSXWSCABLE10002\t2'}
                className="min-h-[84px] font-mono text-[12px]"
                aria-label="Paste part numbers and quantities"
                data-testid="movement-paste"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => setPasteOpen(false)}>Close</Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-ink"
                  disabled={!paste.trim()}
                  onClick={() => { onPasteRows(paste); setPaste(''); setPasteOpen(false); }}
                >
                  Add pasted lines
                </Button>
              </div>
            </div>
          ) : null}
        </Step>

        {deliveryPreview ? (
          <div className="rounded-xl border border-line bg-surface-2 p-4" data-testid="delivery-note-preview">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-ink"><CheckCircle2 className="h-4 w-4 text-status-green-ink" aria-hidden="true" />Delivery Note Preview</p>
                <p className="mt-1 text-[12px] text-ink-3">
                  DN {deliveryPreview.dn_number} · {formatDate(deliveryPreview.date)} · {deliveryPreview.from} → {deliveryPreview.project}
                </p>
                <p className="mt-0.5 text-[12px] text-ink-3">Authorized by: {deliveryPreview.authorized_by}</p>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={onPrintPreview} data-testid="print-dn-btn"><Printer aria-hidden="true" /> Print DN</Button>
                <Button type="button" size="sm" variant="outline" className="text-ink" onClick={onClosePreview}>Close preview</Button>
              </div>
            </div>
            <ul className="mt-3 divide-y divide-line-2 rounded-lg border border-line bg-surface px-3">
              {deliveryPreview.items.map((item) => (
                <li key={item.part_number} className="flex items-center gap-2 py-1.5 text-[12.5px]">
                  <span className="font-mono text-ink">{item.part_number}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-3">{item.description}</span>
                  <b className="tabular-nums">{formatQty(item.qty)}</b>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </form>
    </SidePanel>
  );
}
